import { useMemo, useCallback, useState, useEffect } from 'react';
import { getLoggedinUser } from '../helpers/api_helper';
import {
  ROLES,
  LEGACY_ADMIN_ROLES,
  ASSET_PERMISSIONS,
  SERVICE_PERMISSIONS,
  USER_PERMISSIONS,
  RBAC_PERMISSIONS,
  MAINTENANCE_PERMISSIONS,
  REPORT_PERMISSIONS,
  DEPARTMENT_PERMISSIONS,
} from '../constants/permissions';

/**
 * Hook for checking user permissions throughout the app.
 * @returns {Object} Permission checking methods and user info
 */
const usePermission = () => {
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const handlePermissionsUpdated = () => setRefreshKey((k) => k + 1);
    window.addEventListener('permissions-updated', handlePermissionsUpdated);
    return () => {
      window.removeEventListener('permissions-updated', handlePermissionsUpdated);
    };
  }, []);

  const user = useMemo(() => getLoggedinUser(), [refreshKey]);

  // Get permissions array from user (supports both direct permissions and role-extracted)
  const permissions = useMemo(() => {
    if (!user) return [];

    // Direct permissions array from backend
    if (user.permissions && Array.isArray(user.permissions)) {
      return user.permissions;
    }

    // Extract permissions from roles if available
    if (user.roles && Array.isArray(user.roles)) {
      const perms = new Set();
      user.roles.forEach(role => {
        if (role.permissions && Array.isArray(role.permissions)) {
          role.permissions.forEach(p => {
            perms.add(typeof p === 'string' ? p : p.name);
          });
        }
      });
      return Array.from(perms);
    }

    return [];
  }, [user]);

  // Get permission set for efficient lookups
  const permissionSet = useMemo(() => new Set(permissions), [permissions]);

  // Get user's role (legacy support)
  const role = useMemo(() => user?.role || '', [user]);

  // Get user's roles array
  const roles = useMemo(() => {
    if (!user) return [];
    if (user.roles && Array.isArray(user.roles)) {
      return user.roles;
    }
    // Fallback to single role
    return user.role ? [{ name: user.role }] : [];
  }, [user]);

  /**
   * Check if user has a specific permission.
   * @param {string} permission - The permission to check (e.g., 'asset:read')
   * @returns {boolean}
   */
  const hasPermission = useCallback((permission) => {
    if (!permission) return false;
    return permissionSet.has(permission);
  }, [permissionSet]);

  /**
   * Check if user has any of the specified permissions.
   * @param {string[]} permissionList - Array of permissions to check
   * @returns {boolean}
   */
  const hasAnyPermission = useCallback((permissionList) => {
    if (!permissionList || permissionList.length === 0) return false;
    return permissionList.some(p => permissionSet.has(p));
  }, [permissionSet]);

  /**
   * Check if user has all of the specified permissions.
   * @param {string[]} permissionList - Array of permissions to check
   * @returns {boolean}
   */
  const hasAllPermissions = useCallback((permissionList) => {
    if (!permissionList || permissionList.length === 0) return false;
    return permissionList.every(p => permissionSet.has(p));
  }, [permissionSet]);

  /**
   * Check if user has a specific role.
   * @param {string} roleName - The role name to check (e.g., 'ROLE_ADMINISTRATOR')
   * @returns {boolean}
   */
  const hasRole = useCallback((roleName) => {
    if (!roleName) return false;
    // Check in roles array (handles both string arrays and object arrays)
    if (roles.some(r => {
      // If role is a string
      if (typeof r === 'string') return r === roleName;
      // If role is an object with name property
      if (r && r.name) return r.name === roleName;
      return false;
    })) return true;
    // Check legacy role field
    return role === roleName;
  }, [roles, role]);

  /**
   * Check if user is a super admin.
   * @returns {boolean}
   */
  const isSuperAdmin = useMemo(() => {
    return hasRole(ROLES.SUPER_ADMIN);
  }, [hasRole]);

  /**
   * Check if user is an administrator (includes super admin).
   * @returns {boolean}
   */
  const isAdmin = useMemo(() => {
    return isSuperAdmin ||
           hasRole(ROLES.ADMINISTRATOR) ||
           LEGACY_ADMIN_ROLES.some(r => hasRole(r));
  }, [isSuperAdmin, hasRole]);

  /**
   * Check if user is a requestor.
   * @returns {boolean}
   */
  const isRequestor = useMemo(() => {
    return hasRole(ROLES.REQUESTOR);
  }, [hasRole]);

  // =========================================
  // Feature Access Checks
  // =========================================

  const canAccessAssets = useMemo(() => hasAnyPermission(ASSET_PERMISSIONS), [hasAnyPermission]);
  const canAccessServices = useMemo(() => hasAnyPermission(SERVICE_PERMISSIONS), [hasAnyPermission]);
  const canAccessUsers = useMemo(() => hasAnyPermission(USER_PERMISSIONS), [hasAnyPermission]);
  const canAccessRbac = useMemo(() => hasAnyPermission(RBAC_PERMISSIONS), [hasAnyPermission]);
  const canAccessMaintenance = useMemo(() => hasAnyPermission(MAINTENANCE_PERMISSIONS), [hasAnyPermission]);
  const canAccessReports = useMemo(() => hasAnyPermission(REPORT_PERMISSIONS), [hasAnyPermission]);
  const canAccessDepartments = useMemo(() => hasAnyPermission(DEPARTMENT_PERMISSIONS), [hasAnyPermission]);

  return {
    // User data
    user,
    permissions,
    role,
    roles,

    // Team-scoped asset visibility (informational — the server is the boundary).
    visibilityRestricted: Boolean(user?.visibilityRestricted),
    assetTypeScope: user?.assetTypeScope ?? null,

    // Permission checking methods
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,

    // Role checks
    isSuperAdmin,
    isAdmin,
    isRequestor,

    // Feature access
    canAccessAssets,
    canAccessServices,
    canAccessUsers,
    canAccessRbac,
    canAccessMaintenance,
    canAccessReports,
    canAccessDepartments,
  };
};

export default usePermission;
export { usePermission };
